import type { ContentFeedEntryResolved } from './content-feed.types.ts';

export type ContentSource = ContentFeedEntryResolved['source'];

export interface SuppressionContext {
  sourceToggles: Record<ContentSource, boolean>;
  inGoodStanding: boolean;
}

export interface SuppressionResult {
  entries: ContentFeedEntryResolved[];
  shouldRender: boolean;
}

/**
 * Step 1: Remove entries whose source is toggled off.
 */
export const filterBySourceToggles = (
  entries: ContentFeedEntryResolved[],
  toggles: Record<ContentSource, boolean>,
): ContentFeedEntryResolved[] => {
  return entries.filter((entry) => toggles[entry.source] !== false);
};

/**
 * Determines if an entry is appeal/ad-focused content.
 * Currently: causes entries with funding progress or a metadata link.
 */
const isAppealContent = (entry: ContentFeedEntryResolved): boolean => {
  if (entry.source !== 'causes') {
    return false;
  }

  return !!(entry.metadata?.fundingProgress || entry.metadata?.link);
};

/**
 * Step 2: Remove appeal/ad content when account is not in good standing.
 * Appeal content is identified as 'causes' entries with funding progress or a link.
 */
export const filterByAppealStanding = (
  entries: ContentFeedEntryResolved[],
  inGoodStanding: boolean,
): ContentFeedEntryResolved[] => {
  if (inGoodStanding) {
    return entries;
  }

  return entries.filter((entry) => !isAppealContent(entry));
};

/**
 * Step 3: Evaluate the minimum threshold.
 * Feed renders only if >= 3 entries AND at least one is community.
 */
export const evaluateThreshold = (
  entries: ContentFeedEntryResolved[],
): boolean => {
  if (entries.length < 3) {
    return false;
  }

  return entries.some((entry) => entry.source === 'community');
};

/**
 * Runs the full suppression pipeline in order:
 * 1. Source toggle filtering (configured via Contentful block settings)
 * 2. Appeal content suppression by financial standing
 * 3. Minimum item threshold with community check
 */
export const runSuppressionPipeline = (
  entries: ContentFeedEntryResolved[],
  context: SuppressionContext,
): SuppressionResult => {
  // Step 1: Source toggles
  const afterToggles = filterBySourceToggles(entries, context.sourceToggles);

  // Step 2: Appeal suppression
  const afterAppeal = filterByAppealStanding(
    afterToggles,
    context.inGoodStanding,
  );

  // Step 3: Threshold evaluation
  const shouldRender = evaluateThreshold(afterAppeal);

  return {
    entries: afterAppeal,
    shouldRender,
  };
};

// ─── Personalization Filters ────────────────────────────────────────────────

const MS_PER_DAY = 86_400_000;

/**
 * Excludes entries older than the specified number of days.
 * Entries with missing or null publishedAt are also excluded.
 */
export const filterByRecency = (
  entries: ContentFeedEntryResolved[],
  maxAgeDays = 365,
): ContentFeedEntryResolved[] => {
  const cutoff = Date.now() - maxAgeDays * MS_PER_DAY;

  return entries.filter((entry) => {
    if (!entry.publishedAt) {
      return false;
    }
    return new Date(entry.publishedAt).getTime() >= cutoff;
  });
};

/**
 * Excludes entries that do not have a match object.
 * When includeNearbyEntries is used, all relevant entries should have one.
 * This is a defensive client-side check.
 */
export const filterByMatch = (
  entries: ContentFeedEntryResolved[],
): ContentFeedEntryResolved[] => {
  return entries.filter((entry) => entry.match != null);
};
